import { SortMapping } from '@hidat/huijs-api';
import { FieldConfig } from '@hidat/huijs-common';
import * as Inflector from 'inflected';

/**
 * The OLD Resource Configuration
 * DEPRECIATED
 * Common configuration settings for all resources, can be used by both frontend and backend applications.
 */

/*
export interface ResourceConfigInterface {
    RESOURCE_CODE: string;          // A string code for the resource, generally the name of the class
    API_PATH: string;               // The primary path to manipulate the resource in the api (note, this is just the resource/end part of the path
    APP_PATH?: string;              // The primary path to view this resource in the frontend
    HAS_TENANT: boolean;            // Does the resource require a tenant?
    SOFT_DELETE?: boolean;           // Use soft delete, setting status instead of actually deleting.
    VOCAB_CODE?: string;            // Vocabulary code for the resource on the frontend
    SORTS: SortMapping;             // Valid sort mappings that can be used between the front and backends.  These keys should all be LOWERCASE, the fields should be the JS field names, not the DB names
    DEFAULT_SORT: string;           // The default sort to use if none are specified.
    FILTERS: StringHash;            // Valid filters that can be used between the front and backends
    INCLUDES: StringHash;           // Valid includeParams for the resource that can be used between the front and backends
    REQUIRE_AUTH: boolean;          // Does the resource require authorization?
    FIELDS?: FieldConfig [];
}
*/

/**
 * Resource Configuration
 * Common configuration settings for a resource.
 */
export class ResourceConfig {
  protected _vocabCode: string;
  protected _defaultSort: string | undefined;
  protected _sorts: SortMapping | undefined;
  protected _fields: FieldConfig[] = [];
  protected _softDelete = false;
  public readonly apiPath: string;
  protected _appPath: string;
  protected _editableFields: FieldConfig[] | undefined;
  protected _hashViewNameCache: { [key: string]: string[] } = {};

  constructor(
    public readonly resourceCode: string,
    apiPath?: string,
    public readonly hasTenant = true,
    public readonly requireAuth = true,
  ) {
    // if apiPath is not defined, base it off of the resource code
    if (apiPath) {
      this.apiPath = apiPath;
    } else {
      this.apiPath = Inflector.pluralize(Inflector.underscore(resourceCode));
    }
    this._appPath = this.apiPath;

    this._vocabCode = Inflector.underscore(resourceCode).toUpperCase();
  }

  setVocabCode(code: string): ResourceConfig {
    this._vocabCode = code;
    return this;
  }

  setSorts(sorts: SortMapping): ResourceConfig {
    this._sorts = sorts;
    return this;
  }

  setDefaultSort(sort: string): ResourceConfig {
    this._defaultSort = sort;
    return this;
  }

  setSoftDelete(softDelete = true): ResourceConfig {
    this._softDelete = softDelete;
    return this;
  }

  setAppPath(path: string): ResourceConfig {
    this._appPath = path;
    return this;
  }

  /**
   * Adds the given field to the ResourceConfig
   * By default the FieldConfig will be frozen (and if you are using common FieldConfigs, make sure they are already frozen).
   * Views is one or more hash views that you want to add the FieldConfig to.
   * These will be added before the FieldConfig is frozen.
   * @param fieldConfig
   * @param freeze
   * @param views
   */
  addField(
    fieldConfig: FieldConfig,
    freeze = true,
    views?: string | string[],
  ): ResourceConfig {
    if (fieldConfig) {
      if (views) {
        if (Array.isArray(views)) {
          for (const v of views) {
            fieldConfig = fieldConfig.addToView(v);
          }
        } else {
          fieldConfig = fieldConfig.addToView(views);
        }
      }
      if (freeze) {
        fieldConfig = fieldConfig.freeze();
      }
      this._fields.push(fieldConfig);
    } else {
      throw new Error(
        `Undefined field config passed in ResourceConfig '${this.resourceCode}', field ${this._fields.length}`,
      );
    }
    return this;
  }

  /**
   * Add multiple fields to the ResourceConfig.  This can either be an array of FieldConfigs, or an abject where the
   * values are
   * @param fieldConfigs
   * @param freeze
   * @param views
   */
  addFields(
    fieldConfigs: FieldConfig[] | { [key: string]: FieldConfig },
    freeze = true,
    views?: string | string[],
  ): ResourceConfig {
    let fields: FieldConfig[];
    if (Array.isArray(fieldConfigs)) {
      fields = fieldConfigs;
    } else {
      fields = Object.values(fieldConfigs);
    }
    for (const f of fields) {
      this.addField(f, freeze, views);
    }
    return this;
  }

  get vocabCode(): string {
    return this._vocabCode;
  }

  get fields(): FieldConfig[] {
    return this._fields;
  }

  /**
   * Get Field
   * Return the field with the given name
   * @param fieldName
   */
  public getField(fieldName: string): FieldConfig | undefined {
    return this._fields.find((f) => f.name === fieldName);
  }

  /**
   * Get Fields
   * Returns multiple fields
   * @param nameOrConfig
   */
  public getFields(
    nameOrConfig: string | GetFieldsConfigInterface,
  ): FieldConfig[] | undefined {
    if (typeof nameOrConfig === 'string') {
      const f = this._fields.find((f) => f.name === nameOrConfig);
      if (f) {
        return [f];
      } else {
        return undefined;
      }
    }
    const fields: FieldConfig[] = [];
    if (nameOrConfig.only) {
      if (nameOrConfig.only.length > 0) {
        for (const f of this._fields) {
          const found = nameOrConfig.only.find((n) => f.name === n);
          if (found) {
            fields.push(f);
          }
        }
      }
    } else if (nameOrConfig.except && nameOrConfig.except.length > 0) {
      for (const f of this._fields) {
        const found = nameOrConfig.except.find((n) => f.name === n);
        if (!found) {
          fields.push(f);
        }
      }
    }

    return fields;
  }

  /**
   * Editable Fields
   * Returns all the resource's fields that can be edited.
   */
  editableFields(): FieldConfig[] {
    if (!this._editableFields) {
      this._editableFields = [];
      for (const fc of this._fields) {
        if (fc.canEdit) {
          this._editableFields.push(fc);
        }
      }
    }
    return this._editableFields;
  }

  getView(view: string): string[] {
    let viewNames = this._hashViewNameCache[view];
    if (viewNames) {
      return viewNames;
    }
    viewNames = [];
    for (const fc of this._fields) {
      if (fc.inView(view)) {
        viewNames.push(fc.name);
      }
      this._hashViewNameCache[view] = viewNames;
    }

    return viewNames;
  }

  get defaultSort(): string | undefined {
    return this._defaultSort;
  }

  get sorts(): SortMapping | undefined {
    return this._sorts;
  }

  get appPath(): string {
    return this._appPath;
  }

  get softDelete(): boolean {
    return this._softDelete;
  }
}

/**
 * Config structure for pulling field configs out of the ResourceConfig
 */
export interface GetFieldsConfigInterface {
  only?: string[]; // List of keys to include
  except?: string[]; // List of key to exclude (not valid if only is defined)
  //view?: string;  // Name of the view (not now!)
}
